import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import Breadcrumbs from '@/Layout/Breadcrumbs'
import PageTitle from '@/Layout/PageTitle'

// Public profile page, clones the Featured.com expert layout in
// clcpurple/clcpink. Dark variants re-tune the palette (slate surfaces,
// lighter borders) while preserving the card-on-tinted-bg aesthetic.

const PROFILE_STYLES = `
/* No background-color of its own: the app shell's cream shows through, so the
   profile stops reading as a grey slab dropped onto the page. The brand
   radials stay, they're subtle and work over cream. min-height is gone too;
   it forced a full viewport of colour regardless of content. */
.cc-profile-page {
  background-image:
    radial-gradient(ellipse 80% 60% at 50% 0%,   rgba(153, 98, 231, 0.10), transparent 55%),
    radial-gradient(ellipse 70% 40% at 80% 60%,  rgba(227, 131, 236, 0.10), transparent 50%);
}
.cc-profile-card {
  background: #fff;
  border: 2px solid #000;
  border-radius: 0.5rem;
}
.cc-profile-section-h2 { font-size: 1.875rem; line-height: 1.2; margin-bottom: 1.25rem; font-weight: 600; color: #1a1a1a; }
.cc-profile-sidebar-h3 { font-size: 0.95rem; font-weight: 600; color: #1a1a1a; padding-bottom: 0.25rem; border-bottom: 1px solid #e5e5e5; margin-bottom: 0.75rem; }

.cc-profile-pill {
  display: inline-flex; align-items: center;
  background: #9962E7; color: #fff;
  padding: 0.25rem 0.75rem; border-radius: 9999px;
  font-size: 0.75rem; font-weight: 600;
  white-space: nowrap;
}

.cc-course-card {
  display: flex; flex-direction: column;
  background: #fff;
  border-radius: 0.375rem;
  overflow: hidden;
  min-height: 400px;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.10), 0 8px 10px -6px rgba(0,0,0,0.10);
  transition: transform 300ms cubic-bezier(0.33, 1, 0.68, 1);
  text-decoration: none;
  color: inherit;
}
.cc-course-card:hover { transform: translateY(-4px); }
.cc-course-card .img-wrap { height: 165px; overflow: hidden; }
.cc-course-card img.course-img {
  width: 105%; height: calc(100% + 12px);
  object-fit: cover;
  transition: transform 300ms ease;
}
.cc-course-card:hover img.course-img { transform: translateY(-12px); }

.cc-course-source {
  display: flex; align-items: center; gap: 0.5rem; justify-content: center;
  text-transform: uppercase; color: #603EAA; font-weight: 600;
  font-size: 0.875rem;
  margin: 1.25rem 0;
}

.cc-course-title {
  font-family: 'PP Editorial New', 'Cormorant Garamond', 'EB Garamond', Georgia, serif;
  font-size: 1.875rem; line-height: 1.15; text-align: center;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

/* "View Course" button with the bottom-up fill animation */
.cc-course-cta {
  position: relative; display: inline-block;
  font-weight: 600; font-size: 1rem;
  padding: 0.75rem 2.5rem;
  text-align: center;
  color: #1a1a1a;
}
.cc-course-cta::after {
  content: ""; position: absolute; left: 0; bottom: 0;
  width: 100%; max-height: 0; height: 100%;
  background: #9962E7; border: 4px solid #9962E7;
  display: block; transition: all 300ms ease;
}
.cc-course-card:hover .cc-course-cta::after {
  max-height: 100%; border-radius: 9999px; border-color: #1a1a1a;
}
.cc-course-cta > span { position: relative; z-index: 10; }
.cc-course-card:hover .cc-course-cta > span { color: #fff; }

.cc-avatar-float {
  position: relative;
  height: 6rem; width: 6rem;
  min-height: 6rem; min-width: 6rem;
  flex-shrink: 0;
  aspect-ratio: 1 / 1;
  border-radius: 9999px;
  overflow: hidden;
  background: #f3f3f3;
  border: 2px solid #000;
  transform: translateY(-2.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .cc-course-card, .cc-course-card img.course-img, .cc-course-cta::after { transition: none !important; }
  .cc-course-card:hover { transform: none; }
  .cc-course-card:hover img.course-img { transform: none; }
}

/* Dark mode. Tailwind runs with darkMode: 'class', so .dark on <html> gates these. */
.dark .cc-profile-page {
  background-image:
    radial-gradient(ellipse 80% 60% at 50% 0%,   rgba(153, 98, 231, 0.18), transparent 55%),
    radial-gradient(ellipse 70% 40% at 80% 60%,  rgba(227, 131, 236, 0.14), transparent 50%);
}
.dark .cc-profile-card {
  background: #0f172a;
  border-color: #334155;
}
.dark .cc-profile-section-h2 { color: #f1f5f9; }
.dark .cc-profile-sidebar-h3 { color: #f1f5f9; border-bottom-color: #334155; }
.dark .cc-course-card {
  background: #0f172a;
  box-shadow: 0 20px 25px -5px rgba(0,0,0,0.45), 0 8px 10px -6px rgba(0,0,0,0.35);
}
.dark .cc-course-source { color: #c4b5fd; }
.dark .cc-course-title { color: #f1f5f9; }
.dark .cc-course-cta { color: #f1f5f9; }
.dark .cc-avatar-float { background: #1e293b; border-color: #475569; }
`

const formatRange = (start, end) => {
  if (!start) return ''
  const opts = { month: 'short', year: 'numeric' }
  const s = new Date(start).toLocaleDateString(undefined, opts)
  if (!end) return s
  const e = new Date(end).toLocaleDateString(undefined, opts)
  return s === e ? s : `${s} – ${e}`
}

const splitInterests = (raw) => {
  if (!raw) return []
  return raw.split(/[,\n]/).map(s => s.trim()).filter(Boolean)
}

const Avatar = ({ profile }) => {
  const initial = (profile.firstName?.[0] || profile.username?.[0] || '?').toUpperCase()
  return (
    <div className='cc-avatar-float'>
      {profile.imageUrl ? (
        <img src={profile.imageUrl} alt='' className='h-full w-full object-cover' />
      ) : (
        <div className='h-full w-full flex items-center justify-center text-3xl font-bold text-clcpurple bg-clcpurple/10'>
          {initial}
        </div>
      )}
    </div>
  )
}

const CourseCard = ({ course }) => {
  const statusLabel = course.status === 'completed' ? 'Completed' : 'In Progress'
  return (
    <a href={`/courses/${course.slug}`} className='cc-course-card hover:no-underline'>
      <div className='img-wrap'>
        {course.imageUrl ? (
          <img src={course.imageUrl} alt='' className='course-img' />
        ) : (
          <div className='course-img bg-gradient-to-br from-clcpurple to-clcpink' />
        )}
      </div>
      <p className='cc-course-source'>
        <span className='cc-profile-pill'>{statusLabel}</span>
        {course.startsAtUtc && (
          <span className='text-xs text-gray-500 dark:text-slate-400 normal-case font-normal'>
            {formatRange(course.startsAtUtc, course.endsAtUtc)}
          </span>
        )}
      </p>
      <div className='flex flex-col items-center px-6 pb-6 grow justify-between text-center'>
        <h3 className='cc-course-title'>{course.name}</h3>
        <div>
          <p className='cc-course-cta'><span>View Course</span></p>
        </div>
      </div>
    </a>
  )
}

const Sidebar = ({ profile, interests, stats }) => (
  <aside className='cc-profile-card p-6 space-y-6 self-start'>
    {profile.memberSince && (
      <div>
        <h3 className='cc-profile-sidebar-h3'>Member Since</h3>
        <p className='text-sm text-gray-700 dark:text-slate-300'>
          {new Date(profile.memberSince).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </p>
      </div>
    )}

    {(stats.completed > 0 || stats.inProgress > 0) && (
      <div>
        <h3 className='cc-profile-sidebar-h3'>Stats</h3>
        <ul className='space-y-1 text-sm text-gray-700 dark:text-slate-300'>
          {stats.completed > 0 && (
            <li><strong className='text-clcpurple-dark dark:text-clcpurple'>{stats.completed}</strong> course{stats.completed === 1 ? '' : 's'} completed</li>
          )}
          {stats.inProgress > 0 && (
            <li><strong className='text-clcpurple-dark dark:text-clcpurple'>{stats.inProgress}</strong> in progress</li>
          )}
        </ul>
      </div>
    )}

    {interests.length > 0 && (
      <div>
        <h3 className='cc-profile-sidebar-h3'>Interests</h3>
        <ul className='flex flex-wrap gap-2'>
          {interests.map((tag, i) => (
            <li key={`int-${i}`}>
              <span className='cc-profile-pill'>{tag}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </aside>
)

const ProfilePublic = () => {
  const { username } = useParams()
  const { getRequest } = useQuery()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    setLoading(true)
    setNotFound(false)
    getRequest(`/api/v1/users/public_profile/${username}`, {}, (err, jsonData) => {
      setLoading(false)
      if (err) { setNotFound(true); return }
      setProfile(jsonData)
    })
  }, [username])

  if (loading) return <Loading />

  if (notFound || !profile) {
    return (
      <div className='max-w-2xl mx-auto py-20 text-center'>
        <h1 className='text-3xl font-bold text-slate-900 dark:text-slate-100'>Profile not found</h1>
        <p className='mt-3 text-base text-slate-600 dark:text-slate-400'>
          We couldn't find a Closing Credits member with the username <span className='font-mono'>{username}</span>.
        </p>
      </div>
    )
  }

  const interests = splitInterests(profile.interests)
  const courses = profile.courses || []
  const stats = {
    completed:  courses.filter(c => c.status === 'completed').length,
    inProgress: courses.filter(c => c.status !== 'completed').length,
  }
  const displayName = profile.displayName || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username
  const tagline = courses[0]?.name ? `Studying ${courses[0].name}` : null

  return (
    <>
      <style>{PROFILE_STYLES}</style>

      <Breadcrumbs>
        <span className='text-sm font-medium text-slate-700 dark:text-slate-200'>{displayName}</span>
      </Breadcrumbs>
      <PageTitle pagetitle='Profile' />

      {/* Width and padding come from the layout container, matching every
          other page; this pinned itself to max-w-[1500px] and re-padded. */}
      <div className='cc-profile-page'>
        <div className='py-2 md:py-4'>
          <div className='grid grid-cols-1 lg:grid-cols-[1fr_minmax(300px,25%)] gap-6 lg:gap-10'>

            {/* MAIN CARD */}
            <div className='cc-profile-card p-6 sm:p-10 space-y-10'>

              {/* Hero */}
              <div className='flex flex-col md:flex-row gap-6 mb-2'>
                <Avatar profile={profile} />
                <div className='w-full -mt-6 md:mt-0'>
                  <div className='flex flex-wrap gap-2 items-end'>
                    <h1 className='text-3xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-slate-100 wrap-anywhere'>
                      {displayName}
                    </h1>
                    <em className='text-base text-gray-500 dark:text-slate-400 not-italic'>@{profile.username}</em>
                  </div>
                  {tagline && <p className='pt-2 text-gray-700 dark:text-slate-300'>{tagline}</p>}
                </div>
              </div>

              {/* About */}
              {profile.description && (
                <section>
                  <h2 className='cc-profile-section-h2'>About</h2>
                  <div className='space-y-4 whitespace-pre-line text-pretty text-base md:text-lg text-gray-800 dark:text-slate-200 leading-relaxed'>
                    {profile.description}
                  </div>
                </section>
              )}

              {(profile.description && courses.length > 0) && <hr className='border-gray-200 dark:border-slate-700' />}

              {/* Courses Taken (Featured grid) */}
              {courses.length > 0 && (
                <section>
                  <h2 className='cc-profile-section-h2'>Courses Taken</h2>
                  <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'>
                    {courses.map(c => (
                      <CourseCard key={c.id} course={c} />
                    ))}
                  </div>
                </section>
              )}

              {/* Empty state */}
              {!profile.description && courses.length === 0 && (
                <div className='py-12 text-center text-gray-500 dark:text-slate-400'>
                  {profile.firstName || profile.username} hasn't filled in their profile yet.
                </div>
              )}
            </div>

            {/* SIDEBAR */}
            <Sidebar profile={profile} interests={interests} stats={stats} />

          </div>
        </div>
      </div>
    </>
  )
}

export default ProfilePublic
